import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from '../components/SafeLink';
import Api, { readPhoto } from '../services/api';
import { T, useText } from '../components/Text';
import { usePage } from '../hooks/useResource';

/**
 * Toplu Sipariş Yükleme Sayfası (/order-create.html)
 * Kullanıcıların birden fazla sipariş fotoğrafını toplu olarak seçip yüklemesini sağlar.
 */
export default function Upload() {
  const { t } = useText();
  const navigate = useNavigate();
  usePage(t('Sipariş Oluştur'), 'admin-page orders-page');

  // Form ve yükleme state'leri
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Ortak form alanları
  const [date, setDate] = useState(Api.today());
  const [karat, setKarat] = useState('');
  const [color, setColor] = useState('');

  const itemsRef = useRef([]);
  const guard = useRef(false);
  const alive = useRef(true);
  const saved = useRef(false);
  const running = useRef(false);

  itemsRef.current = items;
  guard.current = uploading || saving || (items.some((i) => i.uploaded) && !saved.current);

  useEffect(() => {
    alive.current = true;
    Api.hasUnsavedChanges = () => guard.current;

    const handleBeforeUnload = (e) => {
      if (guard.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      alive.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      Api.hasUnsavedChanges = null;
      itemsRef.current.forEach((i) => URL.revokeObjectURL(i.url));
    };
  }, []);

  // Dosya seçimi
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setError('');

    const invalid = files.some(
      (f) =>
        !['image/png', 'image/jpeg', 'image/webp'].includes(f.type) ||
        !f.size ||
        f.size > 5 * 1024 * 1024
    );

    if (invalid) {
      e.target.value = '';
      setError('PNG, JPG veya WebP seçin. Her fotoğraf 5 MB veya daha küçük olmalı.');
      return;
    }

    // Önceki URL'leri temizle
    items.forEach((i) => URL.revokeObjectURL(i.url));

    setItems(
      files.map((file) => ({
        file,
        key: Api.createUploadKey(),
        url: URL.createObjectURL(file),
        status: 'pending',
        uploaded: false,
        percent: 0,
        error: '',
      }))
    );
  };

  const updateItem = (key, patch) => {
    if (alive.current) {
      setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
    }
  };

  const removeItem = (key, url) => {
    URL.revokeObjectURL(url);
    setItems((prev) => prev.filter((item) => item.key !== key));
  };

  // Fotoğrafları Firebase Storage'a yükleme
  const handleUpload = async (e) => {
    e.preventDefault();
    if (running.current || !items.length) return;

    running.current = true;
    setUploading(true);
    setError('');

    if (!settings) {
      setSettings({
        customerId: null,
        date,
        karat,
        color,
        completed: false,
        urgent: false,
      });
    }

    try {
      for (const item of items.filter((i) => !i.uploaded)) {
        if (!alive.current) break;
        updateItem(item.key, { status: 'uploading', percent: 0, error: '' });

        try {
          const photo = await readPhoto(item.file);
          await Api.uploadPhoto(photo, item.key, (percent) => updateItem(item.key, { percent }));
          updateItem(item.key, { uploaded: true, status: 'done', percent: 100 });
        } catch (failure) {
          updateItem(item.key, { status: 'error', error: failure.message, percent: 0 });
          setError('Bazı fotoğraflar yüklenemedi. Tekrar denediğinizde yalnızca eksik fotoğraflar yüklenir.');
        }
      }
    } finally {
      running.current = false;
      if (alive.current) setUploading(false);
    }
  };

  // İlerleme hesaplamaları
  const finished = items.filter((i) => i.uploaded).length;
  const complete = items.length > 0 && finished === items.length;
  const percent = items.length
    ? Math.floor(items.reduce((sum, item) => sum + item.percent, 0) / items.length)
    : 0;

  // Siparişleri kaydetme
  const handleSave = async () => {
    if (running.current || saving || !complete) return;

    running.current = true;
    setSaving(true);
    setError('');

    try {
      await Api.mutate('/api/orders/commit-batch', 'POST', {
        ...settings,
        uploadKeys: items.map((i) => i.key),
      });
      saved.current = true;
      guard.current = false;
      navigate('/orders');
    } catch (failure) {
      setError(failure.message + ' Kaydet’e tekrar basabilirsiniz; fotoğraflar yeniden yüklenmez.');
    } finally {
      running.current = false;
      if (alive.current) setSaving(false);
    }
  };

  return (
    <main id="main" className="create-main">
      {/* Başlık ve Geri Dönüş Linki */}
      <div className="orders-heading">
        <div>
          <div className="eyebrow">
            <T>YENİ ÜRETİM</T>
          </div>
          <h1>
            <T>Toplu Sipariş Oluştur</T>
          </h1>
          <p>
            <T>Fotoğrafları yükleyin, sonuçları kontrol edin ve Kaydet'e basın.</T>
          </p>
        </div>
        <Link className="text-link" to="/orders">
          <T>Siparişlere dön ↗</T>
        </Link>
      </div>

      <div className="create-panel">
        <form id="create-order-form" onSubmit={handleUpload}>
          <div className="bulk-layout">
            {/* Sol Panel: Ayarlar */}
            <div className="bulk-settings">
              <div className="unassigned-notice">
                <span className="unassigned-label">
                  <T>Müşteri Seçilmedi</T>
                </span>
                <p>
                  <T>Müşteriyi sipariş detayında sonradan seçeceksiniz.</T>
                </p>
              </div>

              <label className="field">
                <T>Tarih</T>
                <input
                  name="date"
                  type="date"
                  required
                  min="2000-01-01"
                  max="9999-12-31"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={Boolean(settings)}
                />
              </label>

              <p className="bulk-help">
                <T>Müşteri, ayar ve renkleri siparişlerde fotoğraf fotoğraf düzenleyebilirsiniz.</T>
              </p>

              <details className="bulk-defaults">
                <summary>
                  <T>Tüm fotoğraflara ortak ayar / renk ekle</T>
                </summary>

                <label className="field">
                  <T>Ayar</T>
                  <select
                    name="karat"
                    value={karat}
                    onChange={(e) => setKarat(e.target.value)}
                    disabled={Boolean(settings)}
                  >
                    <option value="">{t('Sonradan seçilecek')}</option>
                    <option value="8">{t('8 Ayar')}</option>
                    <option value="9">{t('9 Ayar')}</option>
                    <option value="10">{t('10 Ayar')}</option>
                    <option value="14">{t('14 Ayar')}</option>
                    <option value="18">{t('18 Ayar')}</option>
                    <option value="21">{t('21 Ayar')}</option>
                    <option value="22">{t('22 Ayar')}</option>
                    <option value="Gümüş">{t('Gümüş')}</option>
                  </select>
                </label>

                <label className="field">
                  <T>Renk</T>
                  <select
                    name="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    disabled={Boolean(settings)}
                  >
                    <option value="">{t('Sonradan seçilecek')}</option>
                    <option value="Beyaz">{t('Beyaz')}</option>
                    <option value="Yeşil">{t('Yeşil')}</option>
                    <option value="Roz">{t('Roz')}</option>
                    <option value="Diğer">{t('Diğer')}</option>
                  </select>
                </label>
              </details>
            </div>

            {/* Sağ Panel: Fotoğraf Yükleme Alanı ve Önizlemeler */}
            <div className="bulk-photos">
              <label className="bulk-upload">
                <span id="bulk-upload-label">
                  <T>
                    {complete
                      ? 'Fotoğraflar yüklendi'
                      : settings
                      ? 'Seçilen fotoğraflar'
                      : 'Fotoğrafları seç'}
                  </T>
                </span>
                <input
                  type="file"
                  name="photos"
                  multiple
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleFileSelect}
                  hidden={Boolean(settings)}
                  disabled={uploading || saving}
                />
              </label>

              <p className="photo-help">
                <T>PNG, JPG, WebP · Fotoğraf başına en fazla 5 MB · Fotoğraf sayısı sınırı yok</T>
              </p>

              <div className="bulk-summary">
                <span id="bulk-count" role="status">
                  <T>{items.length ? `${items.length} fotoğraf seçildi` : 'Henüz fotoğraf seçilmedi.'}</T>
                </span>
                {settings && (
                  <span id="bulk-progress-text">
                    <T>{`${finished} / ${items.length} yüklendi · %${percent}`}</T>
                  </span>
                )}
              </div>

              {settings && (
                <progress
                  id="bulk-progress"
                  max={100}
                  value={percent}
                  aria-label={t('Toplu yükleme ilerlemesi')}
                />
              )}

              {/* Önizleme Kartları */}
              <div id="bulk-previews" className="bulk-previews">
                {items.map((item) => (
                  <figure key={item.key} className={`bulk-preview ${item.status}`}>
                    <img src={item.url} alt={item.file.name} loading="lazy" />
                    <figcaption>
                      <span>{item.file.name}</span>
                      <small>
                        <T>
                          {item.status === 'done'
                            ? 'Yüklendi ✓'
                            : item.status === 'error'
                            ? 'Yüklenemedi'
                            : item.status === 'uploading'
                            ? 'Yükleniyor…'
                            : 'Bekliyor'}
                        </T>
                      </small>
                      <progress value={item.percent} max={100} />
                      {item.error && <small className="form-error"><T>{item.error}</T></small>}
                      {!settings && (
                        <button
                          type="button"
                          onClick={() => removeItem(item.key, item.url)}
                        >
                          <T>Kaldır ×</T>
                        </button>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>

          {/* Hata ve Başarı Mesajları */}
          {error && (
            <p className="form-error" id="bulk-error" role="alert">
              <T>{error}</T>
            </p>
          )}

          {complete && (
            <p className="bulk-success" id="bulk-result" role="status">
              <T>
                {`${finished} fotoğrafın tamamı yüklendi ve sunucuda doğrulandı. Kontrol ettikten sonra Kaydet’e basın.`}
              </T>
            </p>
          )}

          {/* Alt Butonlar */}
          <div className="bulk-actions">
            <span className="bulk-help">
              <T>Tüm fotoğraflar yüklendikten sonra Kaydet ile siparişlere aktarılır.</T>
            </span>
            <div className="bulk-action-buttons">
              {!complete && (
                <button
                  type="submit"
                  id="upload-orders"
                  className="button outline"
                  disabled={!items.length || uploading || saving}
                >
                  <T>
                    {uploading
                      ? 'Fotoğraflar yükleniyor…'
                      : settings
                      ? 'Yüklenemeyenleri tekrar dene ↗'
                      : 'Fotoğrafları Yükle ↗'}
                  </T>
                </button>
              )}
              <button
                type="button"
                id="save-batch"
                className="button gold"
                disabled={!complete || uploading || saving}
                onClick={handleSave}
              >
                <T>{saving ? 'Kaydediliyor…' : 'Kaydet ↗'}</T>
              </button>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}
